/**
 * The locale scope of the programmatic surface — station, line and
 * journey pages.
 *
 * Distinct from `LOCALES` on purpose. `LOCALES` is what the APP speaks:
 * the home page, the legal pages and the SPA read `common.json` and the
 * per-screen catalogs, all of which really are translated per region
 * (`en-US` says one thing, `en-GB` another). The programmatic pages read
 * `public/locales/<locale>/prerender.json` and NOTHING else —
 * `prerenderT` binds that single namespace — so their locale scope is
 * decided by that one file.
 *
 * Thirteen locales carry a byte-identical copy of a base locale's
 * `prerender.json`, so they published byte-identical pages under their
 * own prefix: six English copies of every station page, four Spanish,
 * three French. ~24 000 URLs, 15% of the sitemap, on a site with 49 619
 * URLs sitting in «Discovered — currently not indexed». Google was
 * already collapsing them — that is what the «Duplicate, Google chose
 * different canonical» bucket counted on 2026-09-07 (TASK-814 → TASK-875).
 *
 * The pages themselves are NOT deleted from the web: nginx 301s each
 * dropped prefix onto its base for `stations|lines|routes`, because
 * Google already knows these URLs and a missing locale path on this
 * vhost answers 200 with the SPA shell, never 404.
 *
 * Scope is those three surfaces only. The station DETAIL surface
 * (`/<locale>/station/<naptanId>`, `SiteUrls.DETAIL_LOCALES`) reads
 * `station-page.json` plus a per-station description overlay, and those
 * ARE regionally translated — `es-419` detail pages say something
 * different from `es` ones — so the detail surface keeps its prefixes
 * even where this file drops them.
 */
import { LOCALES } from './locales.mjs'

/**
 * Dropped locale → the locale that publishes its catalog.
 *
 * Which member of a duplicate group survives is not arbitrary. It is the
 * one the nginx `$best_locale` map can actually resolve to, because that
 * is where a visitor who sends no locale in the URL is sent: `es`, `fr`,
 * `zh-Hans`, `zh-Hant` are map targets, `es-419`, `fr-FR`, `zh`, `yue`
 * and `zh-HK` are not. Keeping `yue` over `zh-Hant` would have sent every
 * Chinese visitor's first page view through a redirect, into a prefix
 * `hreflang` cannot express: `yue` is not an ISO 639-1 code and bare `zh`
 * does not say which script. `zh-Hant` / `zh-Hans` are also the prefixes
 * `SiteUrls.DETAIL_LOCALES` already publishes, so both surfaces stay on
 * one set of Chinese prefixes instead of disagreeing.
 */
export const DUPLICATE_PROGRAMMATIC_LOCALES = Object.freeze({
  'en-AU': 'en',
  'en-CA': 'en',
  'en-GB': 'en',
  'en-IN': 'en',
  'en-US': 'en',
  'es-419': 'es',
  'es-ES': 'es',
  'es-MX': 'es',
  'fr-CA': 'fr',
  'fr-FR': 'fr',
  yue: 'zh-Hant',
  'zh-HK': 'zh-Hant',
  zh: 'zh-Hans',
})

/**
 * Locales retired from the programmatic surface on 2026-10-08 for lack
 * of demand. In the 28 days to 06.10 the sixteen locales kept below
 * earned 99% of the surface's clicks (en 1 966, es 177, fr 159, it 74,
 * de 33, ja 30, pt 18, nl 18, el 10, pl 8 …) while these 58 earned ≤6
 * each and under 1% together — and held ~110 000 of the URLs sitting in
 * «Discovered — currently not indexed» (70 504 on 04.10) plus most of
 * the «Duplicate, Google chose different canonical» bucket (1 261).
 *
 * Same mechanism as the duplicate table above: nginx 301s
 * `stations|lines|routes` under each prefix onto `en`, so a URL Google
 * already knows answers with a redirect, never with the SPA shell. Home
 * and legal pages stay in all 87 locales; the station DETAIL surface
 * keeps its own `SiteUrls.DETAIL_LOCALES`.
 *
 * Re-admit a locale only with evidence: ≥8 clicks / 28 days on its
 * programmatic pages in Search Console (the bar `pt` and `el` cleared),
 * or a tourist market the app sees in ASC that the surface does not yet
 * serve. `src/seo/programmaticLocaleScope.test.ts` pins the partition.
 */
export const RETIRED_PROGRAMMATIC_LOCALES = Object.freeze(
  Object.fromEntries(
    [
      'am',
      'ar',
      'as',
      'az',
      'be',
      'bg',
      'bn',
      'ca',
      'cs',
      'da',
      'et',
      'fa',
      'fi',
      'fil',
      'gu',
      'ha',
      'he',
      'hi',
      'hr',
      'hu',
      'id',
      'ig',
      'jv',
      'kk',
      'km',
      'kn',
      'ky',
      'lt',
      'lv',
      'mg',
      'ml',
      'mr',
      'ms',
      'my',
      'nb',
      'ne',
      'ny',
      'om',
      'or',
      'pa',
      'ps',
      'ro',
      'sd',
      'sh',
      'sk',
      'sl',
      'so',
      'su',
      'sv',
      'sw',
      'ta',
      'te',
      'th',
      'uk',
      'ur',
      'uz',
      'vi',
      'yo',
    ].map((locale) => [locale, 'en']),
  ),
)

/**
 * The 16 locales station / line / journey pages are prerendered,
 * sitemapped and hreflang-clustered for: `LOCALES` minus the 13
 * duplicates minus the 58 retired. Keeps `LOCALES` order, so
 * `PROGRAMMATIC_LOCALES[0]` is still `DEFAULT_LOCALE`.
 */
export const PROGRAMMATIC_LOCALES = Object.freeze(
  LOCALES.filter(
    (locale) =>
      !(locale in DUPLICATE_PROGRAMMATIC_LOCALES) && !(locale in RETIRED_PROGRAMMATIC_LOCALES),
  ),
)
